import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  TenantModuleDatabaseVm, CreateTenantModuleDatabaseDto,
  UpdateTenantModuleDatabaseDto, ChangeTierDto, ResolveConnectionDto,
  ResolvedConnectionVm, ModuleDatabaseStatus, ModuleTier, ModuleEnvironment,
} from '../models';
import { enumName, enumIndex } from '../utils/enum.util';

@Injectable({ providedIn: 'root' })
export class DatabaseService {
  private http = inject(HttpClient);
  private base(tenantId: string) { return `${environment.apiBaseUrl}/tenants/${tenantId}/databases`; }

  private normalize(db: TenantModuleDatabaseVm): TenantModuleDatabaseVm {
    return {
      ...db,
      environment: enumName(ModuleEnvironment, db.environment),
      status: enumName(ModuleDatabaseStatus, db.status),
      tier: enumName(ModuleTier, db.tier),
    };
  }

  list(tenantId: string, filters?: { status?: ModuleDatabaseStatus; tier?: ModuleTier; moduleCode?: string; environment?: ModuleEnvironment }): Observable<TenantModuleDatabaseVm[]> {
    let hp = new HttpParams();
    const status = enumIndex(ModuleDatabaseStatus, filters?.status);
    const tier = enumIndex(ModuleTier, filters?.tier);
    const env = enumIndex(ModuleEnvironment, filters?.environment);
    if (status != null) hp = hp.set('status', status);
    if (tier != null) hp = hp.set('tier', tier);
    if (env != null) hp = hp.set('environment', env);
    if (filters?.moduleCode) hp = hp.set('moduleCode', filters.moduleCode);
    return this.http.get<TenantModuleDatabaseVm[]>(this.base(tenantId), { params: hp }).pipe(
      map((dbs) => dbs.map((db) => this.normalize(db))),
    );
  }

  create(tenantId: string, dto: CreateTenantModuleDatabaseDto): Observable<TenantModuleDatabaseVm> {
    const body = { ...dto, environment: enumIndex(ModuleEnvironment, dto.environment), tier: enumIndex(ModuleTier, dto.tier) };
    return this.http.post<TenantModuleDatabaseVm>(this.base(tenantId), body).pipe(map((db) => this.normalize(db)));
  }

  update(tenantId: string, dbId: string, dto: UpdateTenantModuleDatabaseDto): Observable<TenantModuleDatabaseVm> {
    return this.http.put<TenantModuleDatabaseVm>(`${this.base(tenantId)}/${dbId}`, dto).pipe(map((db) => this.normalize(db)));
  }

  archive(tenantId: string, dbId: string): Observable<void> {
    return this.http.delete<void>(`${this.base(tenantId)}/${dbId}`);
  }

  // Re-run (idempotent) the schema migration for an existing module database. Resolves on success;
  // on failure the HTTP error body carries { message } with the real reason from the module.
  provision(tenantId: string, dbId: string): Observable<{ provisioned: boolean }> {
    return this.http.post<{ provisioned: boolean }>(`${this.base(tenantId)}/${dbId}/provision`, {});
  }

  changeTier(tenantId: string, dbId: string, dto: ChangeTierDto): Observable<TenantModuleDatabaseVm> {
    const body = { newTier: enumIndex(ModuleTier, dto.newTier) };
    return this.http.patch<TenantModuleDatabaseVm>(`${this.base(tenantId)}/${dbId}/tier`, body).pipe(map((db) => this.normalize(db)));
  }

  resolveConnection(tenantId: string, dto: ResolveConnectionDto): Observable<ResolvedConnectionVm> {
    const body = { ...dto, environment: enumIndex(ModuleEnvironment, dto.environment) };
    return this.http.post<ResolvedConnectionVm>(`${this.base(tenantId)}/resolve`, body).pipe(
      map((r) => ({
        ...r,
        environment: enumName(ModuleEnvironment, r.environment),
        status: enumName(ModuleDatabaseStatus, r.status),
        tier: enumName(ModuleTier, r.tier),
      })),
    );
  }
}
